'use client';

/**
 * AddPaymentMethodScreen Component
 *
 * Handles adding a credit card via Checkout.com Frames.
 * Only used for CC — all other kinds (pp, ap, am, gp) are added directly
 * via addPaymentSource API in usePurchaseFlowHandlers without a setup screen.
 *
 * Calls POST /my/payment_sources, then PUT to get gateway config (tokens),
 * and renders the Checkout Frames form.
 *
 * Note: Header with back/close buttons is rendered by parent PurchaseFlow component.
 */

import React, { useState, useEffect, useCallback } from 'react';
import type { GatewayConfig, PaymentSource } from '../types';
import {
  addPaymentSource, getPaymentSourceConfig, setPaymentSourceAsDefault, ApiError
} from '../api';
import { AddPaymentComponent } from '../gateways/componentSelector';
import { AddPaymentMethodSkeleton } from './AddPaymentMethodSkeleton';
import ErrorPopup from '../components/ErrorPopup';
import { usePurchaseRender } from '../config/PurchaseFlowConfigProvider';
import { PURCHASE_FLOW_ICONS } from '../utils/constants';

const EMPTY_CONFIG = {} as GatewayConfig;

function getErrorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.userMessage;
  if (err instanceof Error) return err.message;
  return '';
}

interface AddPaymentMethodScreenProps {
  /** Called when payment method is successfully added/edited. Passes the new source for local state update. */
  onComplete: (newSource?: PaymentSource) => void;
  onError?: (error: string) => void;
  /**
   * When provided, skips creating a new payment source and instead fetches
   * gateway config for the existing source. Used for editing expired /
   * reauthorize-required cards.
   */
  editPaymentSourceId?: number;
  /** When true, add credit card form shows PAYG disclaimer (old wallet). */
  isPayg?: boolean;
}

const AddPaymentMethodScreen: React.FC<AddPaymentMethodScreenProps> = ({
  onComplete,
  onError,
  editPaymentSourceId,
  isPayg
}) => {
  const [error, setError] = useState<string | null>(null);
  const [paymentSourceId, setPaymentSourceId] = useState<number>(editPaymentSourceId || 0);
  const [gatewayConfig, setGatewayConfig] = useState<GatewayConfig>(EMPTY_CONFIG);
  const [gatewayName, setGatewayName] = useState<string>('ckout');
  const { renderIcon } = usePurchaseRender();
  const isEditMode = !!editPaymentSourceId;
  const hasConfig = gatewayConfig && Object.keys(gatewayConfig).length > 0;
  const isLoading = !hasConfig && !error;

  const handleAddPaymentSource = useCallback(async () => {
    setError(null);

    try {
      let sourceId = editPaymentSourceId;
      let sourceGateway: string | undefined;

      if (!sourceId) {
        const response = await addPaymentSource('cc');

        if (!response?.source?.id || !response?.source?.gateway_name) {
          onError?.('');
          return;
        }

        sourceId = response.source.id;
        sourceGateway = response.source.gateway_name;
        setPaymentSourceId(sourceId);
        setGatewayName(sourceGateway);
      }

      const config = await getPaymentSourceConfig(sourceId);
      if (!config) {
        onError?.('');
        return;
      }

      if (config.gateway_name) {
        setGatewayName(config.gateway_name);
      }
      setGatewayConfig(config);
    } catch (err: unknown) {
      const errorMessage = getErrorMessage(err) || '';
      setError(errorMessage);
      onError?.(errorMessage);
    }
  }, [onError, editPaymentSourceId]);

  // Re-fetch gateway config with fresh tokens (called after error popup OK)
  const handleRefreshConfig = useCallback(async () => {
    if (!paymentSourceId) return;

    try {
      const config = await getPaymentSourceConfig(paymentSourceId);
      if (config) {
        setGatewayConfig(config);
      }
    } catch (err: unknown) {
      const errorMessage = getErrorMessage(err) || '';
      setError(errorMessage);
      onError?.(errorMessage);
    }
  }, [paymentSourceId, onError]);

  useEffect(() => {
    handleAddPaymentSource();
  }, [handleAddPaymentSource]);

  const handleSuccess = async (paymentSource: PaymentSource) => {
    try {
      if (!isEditMode) {
        await setPaymentSourceAsDefault(paymentSource.id);
      }
    } finally {
      onComplete(paymentSource);
    }
  };

  const handleError = (errorMessage: string) => {
    setError(errorMessage);
    onError?.(errorMessage);
  };

  return (
    <>
      {/* Error pop-up for initial load failure (web and mobile) */}
      {error && !hasConfig && (
        <ErrorPopup
          error={ { message: error } }
          onDismiss={ () => setError(null) }
          icon={ renderIcon(PURCHASE_FLOW_ICONS.ERROR) }
          centered={ false }
        />
      )}
      {isLoading && (
        <AddPaymentMethodSkeleton />
      )}
      {hasConfig && (
        <AddPaymentComponent
          gatewayName={ gatewayName }
          gatewayConfig={ gatewayConfig }
          paymentSourceId={ paymentSourceId }
          onSuccess={ handleSuccess }
          onError={ handleError }
          onRefreshConfig={ handleRefreshConfig }
          isPayg={ isPayg }
        />
      )}
    </>
  );
};

export default AddPaymentMethodScreen;
